import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import deepmerge from 'deepmerge';
import Cookies from 'js-cookie';
import { AddressType, SingleAddress } from '@zoocasa/go-search';
import { getTenantById } from '@tenants';
import type { TenantId } from 'tenants/types';
import type { Filter, Sort } from 'contexts/preferences/listing-params/types';
import getTargetedUrl, { type TargetedUrlHelper } from 'utils/targeted-url-helper';
import type {
  AddressViewModel,
  AreaPageV2AreaInsights,
  HybridPageViewModel,
  MilitaryBaseViewModel,
} from 'components/pages/hybrid';
import type { PartialDeep } from 'type-fest';
import { usePreferencesContext } from 'contexts/preferences';
import { useUserContext } from 'contexts/user';
import { useFeaturesContext } from 'contexts/features';
import {
  useModalContext,
  LOGIN_REGISTRATION_MODAL,
} from 'contexts/modal';
import Footer from 'components/footer/footer';
import { AREA_LISTINGS_ROUTE } from 'components/dynamic-page/route-matchers';
import { useSearchResultClick } from 'hooks/use-search-result-click';
import { createSearchPredictionModel } from 'data/search-predictions';
import type SearchPrediction from 'data/search-predictions';
import { useInsightsDerived } from 'components/home-page/themed-home-page-index/homepage-local-focus/use-insights-derived';
import { triggerInsightGenerationIfNeeded, pollForCityInsights, fetchCityLevelInsightsData } from 'components/home-page/themed-home-page-index/homepage-local-focus/fetch-insights';
import { SavedSearchPolicy, savedSearchExtent, recordsItsOwnZoom } from 'domain/saved-search';
import { type ProvinceOrStateCode } from 'utils/province_or_state';
import { CountryCodeList } from 'types/countries';
import { cityFromAreaSlug, provinceFromAreaSlug } from 'utils/area-slug';
import { getCityStats } from 'data/insights/fetcher';
import { zoomForExtent } from 'utils/apple-maps/zoom-for-extent';
import { calculateCenterPoint, getOptimalZoomLevelForHybridPage } from 'components/search-es/geo_helper';
import { formatQueryIntoURLQueryParams } from 'utils/listing-query-helper';
import type { CityStats } from 'data/insights/types';
import type { CountryCode } from 'types/countries';
import type { CityInsightsData } from 'components/home-page/local-focus/city-insights';
import { AreaPageV2View } from './area-page-v2-view';
import type { AreaPageV2Contracts, AreaPageV2Destination } from './contracts';
import type { CollectionsTabId } from './panes/collections-tabs';
import { readRememberedDestination, rememberDestination } from './panes/remembered-destination';
import type { AreaPageV2InsightsContent } from './panes/insights-pane';
import type { ListingsStatsBarData } from './panes/listings-column';
import { getInsightsListingType } from './insights-listing-type';
import type { MapComponentMethods } from 'components/search-es/map/Map';
import generateHeading from 'components/pages/area-listings/util/generate-heading';
import { getAreaLocationFilter } from 'components/pages/area-listings/util/area-location-filter';
import { DEFAULT_LISTING_PARAMS_FILTER_SHARED } from 'contexts/preferences/listing-params/defaults';
import { useAreaPageV2Workspace } from './hooks/use-area-page-v2-workspace';
import { useShowQuebecPopup } from 'hooks/use-quebec-popup';
import { useIsMobile } from 'hooks/use-size-class';
import {
  countryCodeFromProvinceOrState,
  provinceOrStateCodeFromName,
  provinceOrStateCodeFromString,
  provinceOrStateSlugFromCode,
} from 'utils/province_or_state';
import { useCaptureLastSearch } from 'hooks/use-capture-last-search';
import { navigateToAddressMapView } from 'utils/redirect-to-map-view';
import {
  GTM_CLICK_AREA_PAGE_MORE_FILTERS_BUTTON,
  GTM_CLICK_AREA_PAGE_NO_RESULTS,
  GTM_CLICK_AREA_PAGE_SAVE_SEARCH_BUTTON,
  GTM_CLICK_HYBRID_PAGE_RAIL_COLLECTIONS,
  GTM_CLICK_HYBRID_PAGE_RAIL_INSIGHTS,
  GTM_CLICK_HYBRID_PAGE_REMOVE_BOUNDARY,
  GTM_CLICK_HYBRID_PAGE_SAVED_SEARCH_APPLY,
  GTM_MOBILE_FILTER_BAR_ALL_FILTERS_CLICK,
  GTM_MOBILE_FILTER_BAR_SAVE_SEARCH_CLICK,
  GTM_MOBILE_FILTER_BAR_SORT_CHANGED,
  GTM_VIEW_AREA_PAGE,
} from 'constants/events';
import { trackAreaPageV2Event, trackAreaPageV2FilterChange } from './track-area-page-v2-event';
import { MLML_AGENT_COOKIE_NAME } from 'constants/cookies';

export type AreaPageV2ControllerProps = {
  viewModel: HybridPageViewModel<AddressViewModel | MilitaryBaseViewModel>;
  tenantId: TenantId;
};

/** Where a saved search located by the map applies, rather than on an area page. */
const SEARCH_PAGE_PATH = '/search';

/**
 * Whether decoded stats carry nothing.
 *
 * `getCityStats` decodes a protobuf, which fills every field with a default
 * when the service matched nothing, so a miss arrives as a complete object of
 * zeros rather than as null. Callers that only null-check it — including
 * `fetchCityLevelInsightsData`, whose province-wide fallback is written as
 * `if (!stats)` — therefore keep the empty result.
 */
function hasNoCityStats(stats: CityStats | null | undefined): boolean {
  if (!stats) return true;
  return !stats.MedianPurchasePrice
    && !stats.AvgTotalPrice
    && !stats.TotalHouses
    && !stats.TotalCondos
    && !stats.TotalTownhouses
    && !stats.medianPriceTrends?.length;
}

function normalizePath(path: string): string {
  return path.split('?')[0].replace(/\/$/, '') || '/';
}

function isSameAreaPrediction(
  prediction: SearchPrediction,
  currentSlug: string | undefined,
  currentPath: string
): boolean {
  if (prediction.slug && currentSlug && prediction.slug === currentSlug) {
    return true;
  }
  if (prediction.urlPath && normalizePath(prediction.urlPath) === normalizePath(currentPath)) {
    return true;
  }
  return false;
}

export function AreaPageV2Controller({ viewModel, tenantId }: AreaPageV2ControllerProps) {
  const listingsScrollRef = useRef<HTMLElement | null>(null);
  // Imperative handle to the map, used to pan when a saved search is selected.
  const mapRef = useRef<MapComponentMethods | null>(null);
  const pendingAreaPan = useRef<{ zoom: number } | null>(null);
  const router = useRouter();
  const { listingParams } = usePreferencesContext();
  const { siteLocation, isAuthenticated, user } = useUserContext();
  const captureLastSearch = useCaptureLastSearch();
  const { features } = useFeaturesContext();
  const { openModal } = useModalContext();
  const navigateFromSearch = useSearchResultClick(true);
  const [destination, setDestination] = useState<AreaPageV2Destination>('map');
  const [collectionsInitialTab, setCollectionsInitialTab] = useState<CollectionsTabId>('favourites');
  const showQuebecPopup = useShowQuebecPopup();
  const isMobile = useIsMobile();
  const [isSmartSearchOpen, setIsSmartSearchOpen] = useState(false);
  // Held here so the query survives collapsing and can be echoed in the
  // collapsed search field.
  const [smartSearchQuery, setSmartSearchQuery] = useState('');

  // Tenant-level flag; this controller only mounts behind UseAreaPageV2, which
  // keeps smart search scoped to the new area page's search bar.
  const isSmartSearchEnabled = Boolean(getTenantById(tenantId)?.features?.UseSmartSearch);
  const showsMyLinkMyLeadCreate = Boolean(
    viewModel.myLinkMyLead && Cookies.get(MLML_AGENT_COOKIE_NAME)
  );


  const isMilitaryBase = 'militaryBase' in viewModel;
  const addressViewModel = isMilitaryBase
    ? undefined
    : (viewModel as HybridPageViewModel<AddressViewModel>);
  const electedAddress = addressViewModel?.electedAddress;
  const requestedAddress = addressViewModel?.requestedAddress;
  const breadcrumbs = isMilitaryBase ? [] : (addressViewModel?.breadcrumbs ?? []);

  const isFallbackAreaPage = Boolean(
    electedAddress && requestedAddress && requestedAddress.slug !== electedAddress.slug
  );

  useEffect(() => {
    trackAreaPageV2Event(GTM_VIEW_AREA_PAGE);
  }, []);

  useEffect(() => {
    if (isFallbackAreaPage) {
      trackAreaPageV2Event(GTM_CLICK_AREA_PAGE_NO_RESULTS);
    }
  }, [isFallbackAreaPage]);

  const hasQuebecContent = !isMilitaryBase && requestedAddress?.provinceOrState?.slug === provinceOrStateSlugFromCode('qc');

  useEffect(() => {
    showQuebecPopup(Boolean(hasQuebecContent), 'area');
  }, [hasQuebecContent, showQuebecPopup]);
  
  // Add fallback address for area page
  const addressForMapUi = useMemo((): SingleAddress | undefined => {
    if (!electedAddress || !requestedAddress || !isFallbackAreaPage) {
      return electedAddress;
    }
    if (
      requestedAddress.type !== AddressType.ADDRESS_TYPE_SUB_DIVISION &&
      requestedAddress.type !== AddressType.ADDRESS_TYPE_NEIGHBOURHOOD
    ) {
      return electedAddress;
    }
    return SingleAddress.fromPartial({
      ...electedAddress,
      addressType: requestedAddress.type,
      slug: requestedAddress.slug ?? electedAddress.slug,
      neighbourhood: requestedAddress.neighbourhood?.name ?? electedAddress.neighbourhood,
      subDivision: requestedAddress.subDivision?.name ?? electedAddress.subDivision,
    });
  }, [electedAddress, requestedAddress, isFallbackAreaPage]);

  // A saved search for a different area is applied "softly": the URL is rewritten in
  // place (no SSR) and the active area is overridden here so the workspace refetches
  // its polygon + listings. A real navigation changes the SSR address below, which
  // clears the override so the server-elected area wins again.
  const [overrideAddress, setOverrideAddress] = useState<SingleAddress | null>(null);
  const [syncedSsrSlug, setSyncedSsrSlug] = useState(addressForMapUi?.slug);
  if (addressForMapUi?.slug !== syncedSsrSlug) {
    setSyncedSsrSlug(addressForMapUi?.slug);
    setOverrideAddress(null);
  }
  const activeAddress = overrideAddress ?? addressForMapUi;

  // The workspace is remembered for the browser tab session, so a refresh
  // returns to it rather than the map. Storage can only be read after mount,
  // so it cannot seed the initial state without the server and the client
  // disagreeing on the first render.
  const hasRestoredDestination = useRef(false);
  useEffect(() => {
    if (hasRestoredDestination.current || !activeAddress?.slug) {
      return;
    }

    const remembered = readRememberedDestination(activeAddress.slug);

    // Collections needs a signed-in user. A refresh renders before the session
    // resolves, so this waits for it rather than dropping the user on the map.
    if (remembered?.destination === 'collections' && !isAuthenticated) {
      return;
    }

    hasRestoredDestination.current = true;
    if (!remembered) {
      return;
    }

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCollectionsInitialTab(remembered.collectionsTab);
    setDestination(remembered.destination);
  }, [activeAddress?.slug, isAuthenticated]);

  useEffect(() => {
    // Nothing is written until the remembered workspace has been read. The
    // first render is always on the map, so writing before then would
    // overwrite what the refresh is meant to restore.
    if (!hasRestoredDestination.current || !activeAddress?.slug) {
      return;
    }
    rememberDestination({ slug: activeAddress.slug, destination, collectionsTab: collectionsInitialTab });
  }, [activeAddress?.slug, destination, collectionsInitialTab]);
  const isProvinceOrCountryAddress = activeAddress?.addressType === AddressType.ADDRESS_TYPE_PROVINCE_OR_STATE
    || activeAddress?.addressType === AddressType.ADDRESS_TYPE_COUNTRY;

  const mapAreaType =
    overrideAddress?.addressType ?? requestedAddress?.type ?? activeAddress?.addressType;
  const hideMap =
    !isMilitaryBase &&
    (mapAreaType === AddressType.ADDRESS_TYPE_PROVINCE_OR_STATE ||
      mapAreaType === AddressType.ADDRESS_TYPE_COUNTRY);

  // Seed the filter carried in the URL's SEO/query segment (e.g. /filter?bedrooms=2+)
  // into listingParams on first mount, matching the legacy hybrid page. Runs once —
  // later filter changes are user-driven via applyFilters. Without this, a visitor
  // landing directly on such a URL (e.g. via an external MLML redirect) never sees the
  // URL's filter reflected in the filter panel or the initial listings request.
  useEffect(() => {
    const sourceFilter = requestedAddress?.filter;
    if (!sourceFilter) return;
    const areaLocationFilter = getAreaLocationFilter({ requestedAddress, electedAddress });
    const consolidatedFilter = deepmerge(DEFAULT_LISTING_PARAMS_FILTER_SHARED, sourceFilter as Filter);
    listingParams.setFilter({ ...consolidatedFilter, ...areaLocationFilter });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Seed the on-screen area's slug + properly-cased name into the filter, so the search
  // bar, heading, and saved searches read "Toronto, ON" rather than the lowercase
  // slug-derived province — and record this area rather than a stale default.
  // `getAreaLocationFilter` upper-cases the province; `setSlug`/`setAreaName` are each a
  // no-op when unchanged. Runs in an effect keyed on the SSR area (not render-phase, which
  // interfered with the map's fetch cycle); skipped while a soft saved-search switch is
  // active, since that path sets the filter itself.
  useEffect(() => {
    if (overrideAddress) return;
    const { slug: areaSlug, areaName: areaLabel } = getAreaLocationFilter({ requestedAddress, electedAddress });
    if (areaSlug) listingParams.setSlug(areaSlug);
    if (areaLabel) listingParams.setAreaName(areaLabel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressForMapUi?.slug]);

  const workspace = useAreaPageV2Workspace({
    electedAddress: activeAddress,
    listingParams,
    initialListings: viewModel.listings,
    initialPagination: viewModel.pagination,
  });

  const { applyFilters, applySort, refreshWorkspace } = workspace;

  useEffect(() => {
    const pending = pendingAreaPan.current;
    if (!pending || workspace.isMapCenterPointLoading) return;
    pendingAreaPan.current = null;
    mapRef.current?.changeLocation(workspace.mapCenter, pending.zoom);
  }, [workspace.isMapCenterPointLoading, workspace.mapCenter]);

  const areaName = listingParams.filter.areaName
    || activeAddress?.label
    || activeAddress?.neighbourhood
    || activeAddress?.subDivision
    || undefined;

  // On a soft area switch there is no fresh SSR heading, so build one client-side
  // from the active area + applied filters (the same helper the server uses).
  const heading = overrideAddress && activeAddress
    ? generateHeading({
      areaName: areaName ?? '',
      areaType: activeAddress.addressType,
      filter: listingParams.filter,
    })
    : viewModel.headingWithoutCount ?? viewModel.heading;

  // City-level market insights resolved server-side for this area's slug.
  // Applying an area saved search switches areas without re-running SSR, so the
  // server-seeded values are replaced client-side for the area switched to.
  const [insightsOverride, setInsightsOverride] = useState<AreaPageV2AreaInsights | null>(null);
  const areaInsights = insightsOverride ?? viewModel.areaInsights ?? null;
  // The Insights tab follows the rental/sold toggles. SSR seeds the stats for the page's own
  // filters; any other market is fetched from the browser when toggled to, and kept per area
  // and market so toggling back does not refetch.
  const insightsListingType = getInsightsListingType(listingParams.filter);
  const [statsByListingType, setStatsByListingType] = useState<Record<string, CityStats | null>>({});
  const insightsProvinceCode = areaInsights ? provinceOrStateCodeFromString(areaInsights.province) : null;
  const insightsStatsKey = insightsProvinceCode && areaInsights?.country
    ? `${areaInsights.country}:${insightsProvinceCode}:${areaInsights.city}:${insightsListingType}`
    : null;
  const hasSeededStats = Boolean(areaInsights) && areaInsights?.cityStatsListingType === insightsListingType;

  useEffect(() => {
    if (!areaInsights || !insightsProvinceCode || hasSeededStats || !insightsStatsKey || insightsStatsKey in statsByListingType) return;
    const controller = new AbortController();
    getCityStats(
      areaInsights.country,
      insightsProvinceCode,
      areaInsights.city || undefined,
      controller.signal,
      undefined,
      insightsListingType
    )
      .catch(() => undefined)
      .then(stats => {
        if (controller.signal.aborted) return;
        setStatsByListingType(previous => ({ ...previous, [insightsStatsKey]: stats ?? null }));
      });
    return () => controller.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by insightsStatsKey; areaInsights is read for the same key
  }, [insightsStatsKey, hasSeededStats]);

  const insightsCityStats = hasSeededStats
    ? areaInsights?.cityStats ?? null
    : (insightsStatsKey && statsByListingType[insightsStatsKey]) || null;
  const insightsLocationName = areaInsights?.locationName || areaName || '';
  const insightsCitySlug = areaInsights?.citySlug || viewModel.citySlug || '';

  const {
    propertyTypes,
    marketVolume,
    priceTrends,
    marketHealth,
    atAGlanceStats,
  } = useInsightsDerived(
    insightsCityStats,
    [],
    insightsCitySlug,
    Boolean(insightsCityStats),
    // No displayed listing count on purpose. The Insights tab is a market report for the
    // whole area, and the copy around it ("N active listings on the market") never changes
    // with the filters or the map. Feeding it `workspace.pagination.count` made only the
    // Active Listings number move while the sentences beside it stayed put, which read as
    // a contradiction. Omitting it leaves the city-level total from `cityStats`.
    undefined,
    true,
    insightsListingType
  );

  // City Insights are seeded server-side (areaInsights.cityInsights) and, when a
  // row is missing or lacks the request locale's translation, generated on
  // demand and polled for so the section hydrates in place without a reload.
  const [cityInsightsData, setCityInsightsData] = useState<CityInsightsData | null>(
    areaInsights?.cityInsights ?? null
  );
  const [isCityInsightsGenerating, setIsCityInsightsGenerating] = useState(
    Boolean(areaInsights?.citySlug && areaInsights?.insightsGenerateLocale)
  );
  const triggeredInsightGenerationRef = useRef<Set<string>>(new Set());
  const pollAbortRef = useRef<AbortController | null>(null);

  /**
   * Requests the insights text for an area, then polls until it is ready and
   * displays it.
   *
   * This is called from two places. On mount, for the area the server sent.
   * And when the user changes area without a page reload, for the new area.
   *
   * Only one poll runs at a time. Changing area cancels the previous poll. The
   * returned AbortController lets the caller cancel the poll on unmount.
   *
   * `generateLocale` is the locale to generate the text in. For the first area
   * the server provides it. After that it is the locale the user is viewing.
   * The poll always uses the locale the user is viewing, because that is the
   * text to display.
   */
  const generateAndPollInsights = useCallback(({ slug, city, province, country, generateLocale }: {
    slug: string;
    city: string;
    province: string;
    country: CountryCode;
    generateLocale: string | undefined;
  }): AbortController => {
    triggerInsightGenerationIfNeeded(
      slug,
      city,
      province,
      country,
      true,
      generateLocale,
      triggeredInsightGenerationRef.current
    );

    pollAbortRef.current?.abort();
    const controller = new AbortController();
    pollAbortRef.current = controller;

    pollForCityInsights({ slug, locale: router.locale, signal: controller.signal })
      .then(insights => {
        if (controller.signal.aborted) return;
        if (insights) {
          setCityInsightsData(insights);
        }
        setIsCityInsightsGenerating(false);
      })
      .catch(() => setIsCityInsightsGenerating(false));

    return controller;
  }, [router.locale]);

  useEffect(() => {
    const generateLocale = areaInsights?.insightsGenerateLocale ?? null;
    const slug = areaInsights?.citySlug;
    if (!slug || !generateLocale) {
      return;
    }

    const controller = generateAndPollInsights({
      slug,
      city: areaInsights.city,
      province: areaInsights.province,
      country: areaInsights.country,
      generateLocale,
    });

    return () => controller.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once for the server-seeded location
  }, []);

  const insightsContent = useMemo<AreaPageV2InsightsContent>(() => ({
    locationName: insightsLocationName,
    atAGlanceStats,
    propertyTypes,
    marketVolume,
    priceTrends,
    marketHealth,
    cityInsights: cityInsightsData,
    isCityInsightsGenerating,
    listingType: insightsListingType,
  }), [
    insightsLocationName,
    atAGlanceStats,
    propertyTypes,
    marketVolume,
    priceTrends,
    marketHealth,
    cityInsightsData,
    isCityInsightsGenerating,
    insightsListingType,
  ]);

  // Drives the FAQ section under the listings column (always for sale).
  const insightsData = viewModel.areaGuideData?.areaInsightsData?.areaData;
  // The stats bar above the listings shows the same market and stats as the Insights tab.
  const statsBar = useMemo<ListingsStatsBarData>(
    () => ({ insights: insightsCityStats ?? undefined, listingType: insightsListingType }),
    [insightsCityStats, insightsListingType]
  );
  const citySlug = viewModel.citySlug;

  const onLocationSearchClick = useCallback((searchPrediction: SearchPrediction) => {
    const prediction = searchPrediction.transitionToPath
      ? searchPrediction
      : createSearchPredictionModel(searchPrediction);
    const currentSlug = activeAddress?.slug || listingParams.filter.slug || undefined;

    // same area — skip route navigation and soft-refresh map/listings
    if (isSameAreaPrediction(prediction, currentSlug, router.asPath)) {
      refreshWorkspace();
      return;
    }

    navigateFromSearch(searchPrediction);
  }, [
    activeAddress?.slug,
    listingParams.filter.slug,
    navigateFromSearch,
    refreshWorkspace,
    router.asPath,
  ]);

  const syncFilterUrl = useCallback((filters: Filter, sort: Sort) => {
    if (typeof window === 'undefined') {
      return;
    }
    const targetedUrlConfig: TargetedUrlHelper = {
      url: `${window.location.origin}${window.location.pathname}`,
      filters,
      sort,
      pageNumber: 1,
      fallbackSlug: '',
    };
    const targetedUrl = getTargetedUrl(targetedUrlConfig);
    const targetPath = `${targetedUrl.pathname}${targetedUrl.search}`;
    window.history.replaceState(
      { ...window.history.state, as: targetPath, url: targetPath },
      '',
      targetPath
    );
  }, []);

  const onFiltersApplied = useCallback((filter: PartialDeep<Filter>, changedFilter?: string) => {
    trackAreaPageV2FilterChange(changedFilter);
    applyFilters(filter as Filter);
    const consolidatedFilter = deepmerge(listingParams.filter, filter, {
      arrayMerge: (_target, source) => source,
    }) as Filter;
    syncFilterUrl(consolidatedFilter, listingParams.sort);
    if (user?.id) {
      captureLastSearch(user.id, listingParams);
    }
  }, [applyFilters, captureLastSearch, listingParams, syncFilterUrl, user]);

  const onSortChanged = useCallback((sort: Sort) => {
    if (isMobile) {
      trackAreaPageV2Event(GTM_MOBILE_FILTER_BAR_SORT_CHANGED, sort);
    }
    const sortChanged = listingParams.sort !== sort;
    applySort(sort);
    syncFilterUrl(listingParams.filter, sort);
    if (sortChanged && user?.id) {
      captureLastSearch(user.id, listingParams);
    }
  }, [applySort, captureLastSearch, isMobile, listingParams, syncFilterUrl, user]);

  /**
   * Replaces the insights shown with the ones for `slug`, for a soft area
   * switch. The page's own insights come from SSR, which does not re-run here,
   * so they are fetched from the browser instead. Province is read off the area
   * slug, falling back to the current area's when the slug does not carry one.
   */
  const refreshInsightsForArea = useCallback(async (slug: string) => {
    const city = cityFromAreaSlug(slug);
    const province = provinceFromAreaSlug(slug) ?? viewModel.areaInsights?.province;
    const country = viewModel.areaInsights?.country ?? siteLocation;
    if (!province || !country) return;

    const data = await fetchCityLevelInsightsData(country as CountryCode, province, city, slug, router.locale)
      .catch(() => null);
    if (!data) return;

    // A city the service does not match decodes to a complete object of zeros,
    // which `fetchCityLevelInsightsData` accepts as a hit, so its own
    // province-wide fallback (written as `if (!stats)`) never runs. Asking for
    // the province directly gives the panel real numbers instead of a grid of
    // zeros.
    const stats = hasNoCityStats(data.stats)
      ? (await getCityStats(country as CountryCode, province as ProvinceOrStateCode).catch(() => null)) ?? null
      : data.stats;

    setInsightsOverride({
      cityStats: stats,
      cityStatsListingType: 'sale',
      cityInsights: data.cityInsights,
      insightsGenerateLocale: null,
      locationName: city,
      locationUrl: `/${slug}-real-estate`,
      citySlug: slug,
      city,
      province,
      country: country as CountryCode,
    });
    setCityInsightsData(data.cityInsights);

    // The mount-time effect only generates for the server-seeded area, so an
    // area switched into asks for its own prose when none is stored yet.
    if (data.insightsTranslationMissing) {
      setIsCityInsightsGenerating(true);
      generateAndPollInsights({
        slug,
        city,
        province,
        country: country as CountryCode,
        generateLocale: router.locale,
      });
    } else {
      setIsCityInsightsGenerating(false);
    }
  }, [router.locale, siteLocation, viewModel.areaInsights, generateAndPollInsights]);

  const onApplySavedSearch = useCallback((filter: Filter) => {
    trackAreaPageV2Event(GTM_CLICK_HYBRID_PAGE_SAVED_SEARCH_APPLY);
    // Apply the saved search's filters. They are stored on the shared
    // listing-params object. This runs first in all cases, including the ones
    // that leave this page, because the query string below is built from those
    // params.
    applyFilters({
      ...filter,
      smartTagNames: filter.smartTagNames ?? [],
    });

    console.log('filter', filter);

    // This search has no named area, so this page cannot show it. The search
    // page is opened instead.
    //
    // The search page centres its map on the filter's latitude and longitude.
    // This kind of search does not store those. It stores a `bbox` or a
    // `boundary` instead. So the centre of that area is calculated and written
    // to the filter before leaving this page.
    if (!SavedSearchPolicy.isAreaSearch(filter)) {
      const extent = savedSearchExtent(filter);
      if (extent) {
        const center = calculateCenterPoint(extent);
        listingParams.setLatitude(center.lat);
        listingParams.setLongitude(center.lng);
        // The zoom is calculated from the saved area, unless the filter's own
        // `zoom` value is correct (see `recordsItsOwnZoom`). An incorrect
        // value would show the wrong number of listings.
        if (!recordsItsOwnZoom(filter)) {
          listingParams.setZoom(zoomForExtent(extent));
        }
      }
      if (typeof window !== 'undefined') {
        // The filter is put in the query string. Every other saved-search link
        // opens the search page the same way (`SavedSearch.path`, the header
        // dropdown, `navigateToSearchSuggestion`).
        //
        // A plain `/search` URL does not work. If there is no query string, the
        // preferences provider replaces the listing params with the user's
        // stored last search. The applied search is then lost.
        //
        // On arrival, the search page copies the query string into the listing
        // params and removes it from the URL
        // (`consumeSearchUrlFilterParams`). So the address bar still shows
        // `/search`.
        const query = `${formatQueryIntoURLQueryParams(listingParams.filter)}&sort=${listingParams.sort}`;
        window.location.assign(`${SEARCH_PAGE_PATH}?${query}`);
      }
      return;
    }

    // A saved search carries the area it was saved for. When that differs from the
    // area we're viewing, soft-switch to it: override the active area so the workspace
    // refetches its polygon + listings, and rewrite the URL bar in place via
    // history.replaceState — no Next navigation, so getServerSideProps does not re-run
    // and nothing remounts. (Mirrors the hybrid page's URL-in-place trick.)
    const switchedArea = Boolean(filter.slug && filter.slug !== activeAddress?.slug);
    const labelParts = (filter.areaName ?? '').split(',').map(part => part.trim()).filter(Boolean);
    const trailing = labelParts[labelParts.length - 1];
    const provinceFromLabel = trailing
      ? (provinceOrStateCodeFromString(trailing) ?? provinceOrStateCodeFromName(trailing) ?? undefined)
      : undefined;
    const province = provinceFromLabel ?? (filter.slug ? provinceFromAreaSlug(filter.slug) : undefined);
    const namedParts = provinceFromLabel ? labelParts.slice(0, -1) : labelParts;
    const neighbourhood = namedParts.length >= 2 ? namedParts[namedParts.length - 2] : undefined;
    const subDivision = namedParts.at(-1)
      ?? (filter.slug && provinceFromAreaSlug(filter.slug) ? cityFromAreaSlug(filter.slug) : undefined);
    const countryCode = countryCodeFromProvinceOrState(province ?? '');
    const override = filter.slug
      ? SingleAddress.fromPartial({
        slug: filter.slug,
        label: filter.areaName?.trim() || subDivision || '',
        neighbourhood,
        subDivision,
        provinceOrState: province,
        country: countryCode === CountryCodeList.UNITED_STATES ? 'United States' : 'Canada',
        countryCode,
        addressType: neighbourhood
          ? AddressType.ADDRESS_TYPE_NEIGHBOURHOOD
          : subDivision
            ? AddressType.ADDRESS_TYPE_SUB_DIVISION
            : province
              ? AddressType.ADDRESS_TYPE_PROVINCE_OR_STATE
              : AddressType.ADDRESS_TYPE_SUB_DIVISION,
      })
      : null;
    const zoom = filter.zoom || (override ? getOptimalZoomLevelForHybridPage(override) : undefined);

    if (switchedArea && override && filter.slug) {
      setOverrideAddress(override);
      if (typeof window !== 'undefined') {
        const targetPath = `/${filter.slug}-real-estate`;
        window.history.replaceState(
          { ...window.history.state, as: targetPath, url: targetPath },
          '',
          targetPath
        );
      }
      // Insights describe the area the page loaded for, and SSR does not re-run
      // on a soft switch, so they are refetched for the area switched to.
      void refreshInsightsForArea(filter.slug);
    }

    // Applying is only visible on the map, and a search can be applied from the
    // Collections destination, so show the map rather than leaving the user on
    // the list they clicked from.
    setDestination('map');

    // Pan the map to the saved location (the map ignores center prop changes once
    // ready, so a soft area switch still needs this imperative move).
    if (filter.latitude && filter.longitude) {
      pendingAreaPan.current = null;
      mapRef.current?.changeLocation(
        { lat: filter.latitude, lng: filter.longitude },
        zoom ?? filter.zoom
      );
    } else if (switchedArea && zoom) {
      pendingAreaPan.current = { zoom };
    }
  }, [applyFilters, activeAddress?.slug, refreshInsightsForArea, listingParams]);

  const userId = user?.id;
  const onRemoveBoundary = useCallback(() => {
    if (!activeAddress) return;
    trackAreaPageV2Event(GTM_CLICK_HYBRID_PAGE_REMOVE_BOUNDARY);
    // Unlike the hybrid page's equivalent handler, explicitly drop the slug:
    // SearchPageV2's workspace (unlike legacy /search) scopes its first fetch by
    // slug when present, so a stale one here would show area-scoped results
    // instead of the free-bounds view this button is supposed to produce.
    listingParams.setSlug('');
    // Without a boundary the search is no longer tied to a named area, so clear the
    // name too; the search bar then shows its placeholder instead of a stale area.
    listingParams.setAreaName('');
    const position = workspace.getCurrentPosition();
    // For a signed-in user, a plain `/search` makes the preferences provider replace the
    // listing params with the stored last search, which still names the area searched
    // before. Store this search first (the helper has set the map position by the time it
    // pushes), so what the search page reads back is the boundary-free view.
    const push: typeof router.push = async (...args) => {
      if (userId) {
        await captureLastSearch(userId, listingParams);
      }
      return router.push(...args);
    };
    void navigateToAddressMapView(listingParams, activeAddress, push, siteLocation, position);
  }, [activeAddress, captureLastSearch, listingParams, siteLocation, router, userId, workspace]);

  const onOpenSavedSearches = useCallback(() => {
    trackAreaPageV2Event(GTM_CLICK_AREA_PAGE_SAVE_SEARCH_BUTTON);
    if (isMobile) {
      trackAreaPageV2Event(GTM_MOBILE_FILTER_BAR_SAVE_SEARCH_CLICK);
    }
  }, [isMobile]);

  const onFiltersPanelOpenChange = useCallback((isOpen: boolean) => {
    if (!isOpen) {
      return;
    }
    trackAreaPageV2Event(GTM_CLICK_AREA_PAGE_MORE_FILTERS_BUTTON);
    if (isMobile) {
      trackAreaPageV2Event(GTM_MOBILE_FILTER_BAR_ALL_FILTERS_CLICK);
    }
  }, [isMobile]);

  // Open-only: the panel's own outside-click handler owns closing, so a toggle
  // here would fight it and leave the panel stuck open.
  const onOpenSmartSearch = useCallback(() => {
    setIsSmartSearchOpen(true);
  }, []);

  const onCloseSmartSearch = useCallback(() => {
    setIsSmartSearchOpen(false);
  }, []);

  const onOpenMap = useCallback(() => {
    setDestination('map');
  }, []);

  // Opens the Collections view on `tab`. Collections needs a signed-in user,
  // so a signed-out user is asked to sign in first and the view opens after.
  const openCollections = useCallback((tab: CollectionsTabId) => {
    const open = () => {
      setCollectionsInitialTab(tab);
      setDestination('collections');
    };
    if (isAuthenticated) {
      open();
      return;
    }
    openModal(LOGIN_REGISTRATION_MODAL, { callback: open });
  }, [isAuthenticated, openModal]);

  const onOpenCollections = useCallback(() => {
    trackAreaPageV2Event(GTM_CLICK_HYBRID_PAGE_RAIL_COLLECTIONS);
    openCollections('favourites');
  }, [openCollections]);

  // "All Saved Searches" in the saved-search panel. The Collections view lists
  // every saved search on its own tab, so it is opened there instead of the
  // account page.
  const onViewAllSavedSearches = useCallback(() => {
    openCollections('saved-searches');
  }, [openCollections]);

  const onOpenInsights = useCallback(() => {
    trackAreaPageV2Event(GTM_CLICK_HYBRID_PAGE_RAIL_INSIGHTS);
    setDestination('insights');
  }, []);

  const contracts: AreaPageV2Contracts = {
    areaName,
    destination,
    filter: listingParams.filter,
    filtersResultsCount: workspace.pagination.count,
    onOpenSavedSearches,
    isSmartSearchEnabled,
    isSmartSearchOpen: isSmartSearchEnabled && isSmartSearchOpen,
    smartSearchQuery,
    onSmartSearchQueryChange: setSmartSearchQuery,
    onOpenSmartSearch,
    onCloseSmartSearch,
    onOpenCollections,
    onViewAllSavedSearches,
    collectionsInitialTab,
    onCollectionsTabChange: setCollectionsInitialTab,
    onOpenMap,
    onOpenInsights,
    onLocationSearchClick,
    onFiltersApplied,
    onCountListingsForFilter: workspace.countListingsForFilter,
    onSortChanged,
    onApplySavedSearch,
    onFiltersPanelOpenChange,
    listingsScrollRef,
  };

  // US feeds require each provider's compliance disclaimer alongside the results
  // they supplied, so US areas show it under the listings column. Mirrors the
  // hybrid page's `showDisclaimer`, including that there is nothing to disclaim
  // when the area returned no listings. The military-base arm is carried over
  // from there for when this page starts serving them — today it bails out above,
  // because those view models have no elected address.
  const showProviderDisclaimer = (
    isMilitaryBase || activeAddress?.countryCode === CountryCodeList.UNITED_STATES
  ) && workspace.pagination.count > 0;

  const footerSlot = (
    <Footer
      canShowAccessibilityWidget
      tenantId={tenantId}
      countryCode={siteLocation}
      route={AREA_LISTINGS_ROUTE}
      features={features}
    />
  );

  if (!activeAddress) {
    return null;
  }

  return (
    <AreaPageV2View
      contracts={contracts}
      insights={insightsContent}
      electedAddress={activeAddress}
      heading={heading}
      listings={workspace.listings}
      pagination={workspace.pagination}
      listingParams={listingParams}
      footerSlot={footerSlot}
      tenantId={tenantId}
      showsMyLinkMyLeadCreate={showsMyLinkMyLeadCreate}
      isCrawler={viewModel.isCrawler}
      hideMap={hideMap}
      citySlug={citySlug}
      insightsData={insightsData}
      statsBar={statsBar}
      breadcrumbs={breadcrumbs}
      showProviderDisclaimer={showProviderDisclaimer}
      clusters={workspace.clusters}
      highlightedListingId={workspace.highlightedListingId}
      isMapLoading={workspace.isMapLoading}
      areaPolygonWkt={workspace.areaPolygonWkt}
      mapCenter={workspace.mapCenter}
      mapZoom={workspace.mapZoom}
      mapRef={mapRef}
      onRemoveBoundary={!isProvinceOrCountryAddress ? onRemoveBoundary : undefined}
      isMapCenterPointLoading={workspace.isMapCenterPointLoading}
      isMapCenterPointError={workspace.isMapCenterPointError}
      onMapBoundingRegionChanged={workspace.onMapBoundingRegionChanged}
      onMapZoomChanged={workspace.onMapZoomChanged}
      onListingHover={workspace.onListingHover}
      setPageNumber={workspace.setPageNumber}
    />
  );
}
